import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

function Privacy() {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <h1>Privacy Policy</h1>
        <div className="legal-updated">Last updated: July 20, 2026</div>
      </section>

      <div className="legal-container">
        <p className="legal-intro">
          This Privacy Policy explains how Alooma Media Group ("Alooma," "we," "us," or "our")
          collects, uses, discloses, and safeguards your information when you use Alooma Network,
          our creator–brand marketplace available at{' '}
          <a href="https://alooma.mg">alooma.mg</a> and related services (the "Service"). By creating
          an account or using the Service, you agree to this Policy.
        </p>

        <nav className="legal-toc">
          <h2>Contents</h2>
          <ol>
            <li><a href="#info-we-collect">Information We Collect</a></li>
            <li><a href="#social-data">Social Media Account Data</a></li>
            <li><a href="#how-we-use">How We Use Information</a></li>
            <li><a href="#how-we-share">How We Share Information</a></li>
            <li><a href="#platform-compliance">Platform Compliance</a></li>
            <li><a href="#retention">Data Retention &amp; Deletion</a></li>
            <li><a href="#security">Data Security</a></li>
            <li><a href="#your-rights">Your Rights &amp; Choices</a></li>
            <li><a href="#cookies">Cookies &amp; Local Storage</a></li>
            <li><a href="#children">Children's Privacy</a></li>
            <li><a href="#international">International Users</a></li>
            <li><a href="#changes">Changes to This Policy</a></li>
            <li><a href="#contact">Contact Us</a></li>
          </ol>
        </nav>

        <section className="legal-section" id="info-we-collect">
          <h2>1. Information We Collect</h2>
          <p>We collect the following categories of information:</p>
          <h3>Account information</h3>
          <ul>
            <li>Email address and a securely hashed password.</li>
            <li>Account type (brand or creator).</li>
          </ul>
          <h3>Profile information</h3>
          <ul>
            <li><strong>Creators:</strong> full name, biography, content niches, location, birthday, social handles, rate card, past partnerships, and optional payment method details you choose to provide.</li>
            <li><strong>Brands:</strong> company name, website, industry, location, contact email, and brand description.</li>
          </ul>
          <h3>Marketplace activity</h3>
          <ul>
            <li>Job postings, bids and proposals, invitations, and the messages you exchange with other users.</li>
            <li>Notifications generated by your activity on the Service.</li>
          </ul>
          <h3>Technical information</h3>
          <ul>
            <li>Authentication tokens stored in your browser, and basic log/diagnostic data used to operate and secure the Service.</li>
          </ul>
        </section>

        <section className="legal-section" id="social-data">
          <h2>2. Social Media Account Data</h2>
          <p>
            Creators may optionally connect third-party social media accounts —
            Instagram, Facebook, TikTok, YouTube, X (Twitter), and Snapchat — using each platform's
            official OAuth authorization. If you choose to connect an account, we access and store only
            the data those platforms make available for the permissions you grant, which may include:
          </p>
          <ul>
            <li>Platform username, display name, profile picture, and account type.</li>
            <li>Public metrics such as follower/subscriber counts, post/video counts, average likes and comments, and engagement rate.</li>
            <li>Aggregated, non-identifying audience insights where a platform provides them.</li>
            <li>OAuth access and refresh tokens needed to retrieve the above (stored securely and, where enabled, encrypted at rest).</li>
          </ul>
          <p>
            We use this data solely to display verified reach and engagement on your Alooma profile so
            brands can evaluate potential partnerships. You can disconnect any platform at any time from
            your profile page, which revokes our stored tokens and removes the associated metrics.
            We do not post to your social accounts, and we do not access your private messages on those platforms.
          </p>
        </section>

        <section className="legal-section" id="how-we-use">
          <h2>3. How We Use Information</h2>
          <ul>
            <li>Operate the marketplace: match creators and brands, power job postings, bids, invitations, and messaging.</li>
            <li>Display your profile and verified social metrics to relevant brands.</li>
            <li>Send transactional notifications (in-app and email) such as new bids, accepted bids, new messages, and invitations.</li>
            <li>Authenticate users, maintain security, prevent fraud and abuse, and comply with legal obligations.</li>
            <li>Improve and troubleshoot the Service.</li>
          </ul>
        </section>

        <section className="legal-section" id="how-we-share">
          <h2>4. How We Share Information</h2>
          <p>We do <strong>not</strong> sell your personal information. We share information only as follows:</p>
          <ul>
            <li><strong>With other users:</strong> your profile, verified social metrics, bids, and messages are shared with the brands or creators you interact with on the Service.</li>
            <li><strong>Service providers:</strong> infrastructure and hosting (Amazon Web Services), and transactional email delivery providers, acting on our behalf under confidentiality obligations.</li>
            <li><strong>Legal &amp; safety:</strong> when required by law, or to protect the rights, property, or safety of Alooma, our users, or the public.</li>
            <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, subject to this Policy.</li>
          </ul>
        </section>

        <section className="legal-section" id="platform-compliance">
          <h2>5. Platform Compliance (Meta, Google, TikTok, and others)</h2>
          <p>
            Our use and transfer of information received from social platform APIs adhere to each
            provider's developer policies, including the Meta Platform Terms and Developer Policies, the
            Google API Services User Data Policy (including the Limited Use requirements), the TikTok
            Developer Terms, and the X and Snap developer terms. We only request the minimum permissions
            necessary, use the data solely to provide user-facing features described in this Policy, and do
            not use it for advertising or sell it to third parties.
          </p>
        </section>

        <section className="legal-section" id="retention">
          <h2>6. Data Retention &amp; Deletion</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide the
            Service. You may:
          </p>
          <ul>
            <li>Disconnect any social account at any time to remove its tokens and stored metrics.</li>
            <li>Edit or remove profile details from your dashboard.</li>
            <li>Request deletion of your account and associated personal data by emailing us (see Contact). We will delete or de-identify your data except where retention is required by law.</li>
          </ul>
        </section>

        <section className="legal-section" id="security">
          <h2>7. Data Security</h2>
          <p>
            We use industry-standard measures to protect your information, including encrypted transport
            (HTTPS), hashed passwords, token-based authentication, and access controls. Where enabled,
            stored OAuth tokens are encrypted at rest. No method of transmission or storage is completely
            secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section className="legal-section" id="your-rights">
          <h2>8. Your Rights &amp; Choices</h2>
          <p>
            Depending on your location, you may have rights to access, correct, delete, or export your
            personal information, or to object to or restrict certain processing (for example under the
            GDPR or CCPA/CPRA). To exercise these rights, contact us using the details below. We will not
            discriminate against you for exercising your rights.
          </p>
        </section>

        <section className="legal-section" id="cookies">
          <h2>9. Cookies &amp; Local Storage</h2>
          <p>
            We use browser local storage to keep you signed in (authentication token and basic profile
            data) and to operate core features. We do not use third-party advertising cookies. You can
            clear this data through your browser settings, though doing so will sign you out.
          </p>
        </section>

        <section className="legal-section" id="children">
          <h2>10. Children's Privacy</h2>
          <p>
            The Service is not directed to individuals under 18, and we do not knowingly collect personal
            information from children. If you believe a minor has provided us information, please contact us
            and we will delete it.
          </p>
        </section>

        <section className="legal-section" id="international">
          <h2>11. International Users</h2>
          <p>
            Alooma operates from the United States. If you access the Service from outside the U.S., you
            understand your information may be processed and stored in the U.S., where data protection laws
            may differ from those in your country.
          </p>
        </section>

        <section className="legal-section" id="changes">
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Policy from time to time. We will post the revised version here and update
            the "Last updated" date. Material changes may be communicated through the Service.
          </p>
        </section>

        <section className="legal-section" id="contact">
          <h2>13. Contact Us</h2>
          <p>
            Questions or requests regarding this Policy or your data can be sent to{' '}
            <a href="mailto:info@aloomamediagroup.com">info@aloomamediagroup.com</a>.
          </p>
        </section>
      </div>

      <footer className="legal-footer">
        <p>©2026 Alooma Media Group. All rights reserved.</p>
        <p>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/">Home</Link>
        </p>
      </footer>
    </div>
  );
}

export default Privacy;
